import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { CheckToken } from "../login/AccessToken";

const REACT_APP_BACKEND_ROUTE_URL = process.env.REACT_APP_BACKEND_ROUTE_URL;

function doctorName(doctor) {
  return String(doctor?.name || "").trim();
}

/**
 * "Dr. Sandeep Sen" from the API's `salutation` + `name`. Null/blank salutations fall back
 * to the bare name, and a name that already starts with the salutation isn't prefixed twice.
 */
export function formatDoctorName(doctor) {
  const name = doctorName(doctor);
  const raw = String(doctor?.salutation || "").trim();
  if (!raw || !name) return name;
  const salutation = /^[A-Za-z]+$/.test(raw) ? `${raw}.` : raw;
  const bare = (text) => text.replace(/\./g, "").trim().toLowerCase();
  const first = name.split(/\s+/)[0];
  if (bare(first) === bare(salutation)) return name;
  return `${salutation} ${name}`;
}

function normalizeDoctors(list) {
  const seen = new Set();
  const doctors = [];
  (Array.isArray(list) ? list : []).forEach((doctor) => {
    const name = doctorName(doctor);
    const doctorId = doctor.doctor_id || doctor.dr_id || "";
    const key = String(doctorId || name).toLowerCase();
    if (!name || seen.has(key)) return;
    seen.add(key);
    doctors.push({
      name,
      doctor_id: doctorId,
      salutation: String(doctor.salutation || "").trim(),
      specialization: String(doctor.specialization || "").trim(),
    });
  });
  return doctors;
}

async function fetchFacilityDoctors(token) {
  const headers = { Authorization: `Bearer ${token}` };
  const endpoints = [
    "get_doctors_based_on_facility_user/",
    "get_all_doctors/?scope=facility",
  ];

  for (const endpoint of endpoints) {
    try {
      const res = await axios.get(`${REACT_APP_BACKEND_ROUTE_URL}${endpoint}`, {
        headers,
      });
      if (res?.data?.status !== "Success") continue;
      const list = res.data?.data ?? res.data?.doctor_list ?? [];
      if (list.length) return normalizeDoctors(list);
    } catch (err) {
      console.warn(`Doctor list endpoint failed: ${endpoint}`, err);
    }
  }

  return [];
}

/**
 * Loads the facility doctor list once. Call this in the page and hand the result to
 * ReferredByDoctorField / OrderedByDoctorSelect so one screen makes a single request.
 * Pass `enabled = false` to skip the fetch (the caller already has the list).
 */
export function useFacilityDoctors(enabled = true) {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const token = sessionStorage.getItem("accessToken") || "";
    if (!token) return undefined;

    CheckToken();
    setLoading(true);
    fetchFacilityDoctors(token)
      .then((list) => {
        if (!cancelled) setDoctors(list);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { doctors, loading };
}

/** `referred_by` is stored as a comma-separated string, so commas inside a name are dropped. */
export function cleanName(name) {
  return String(name || "")
    .replace(/,/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Non-doctor referral sources, listed first in Referred By. They never sit on invoice lines. */
export const REFERRAL_SOURCES = [
  { doctor_id: "__self__", name: "Self" },
  { doctor_id: "__external__", name: "External" },
];

export const isReferralSource = (id) =>
  REFERRAL_SOURCES.some((source) => source.doctor_id === id);

/** A name typed by the user is held as "custom:<name>" in the Referred By ids. */
const CUSTOM_PREFIX = "custom:";
export const CUSTOM_OPTION = { doctor_id: "__custom__", name: "Custom name..." };

export const isCustomReferral = (id) => String(id).startsWith(CUSTOM_PREFIX);

/** Self, External and custom names are not doctors, so they never touch invoice lines. */
export const isNonDoctorReferral = (id) =>
  isReferralSource(id) || isCustomReferral(id);

/** Like referralName, but a facility doctor comes with the salutation ("Dr. Sandeep Sen"). */
export function referralLabel(id, doctors) {
  if (isCustomReferral(id) || isReferralSource(id)) {
    return referralName(id, doctors);
  }
  const doctor = doctors.find((d) => String(d.doctor_id) === String(id));
  return doctor ? formatDoctorName(doctor) : undefined;
}

/** Display name for a Referred By id: a custom name, a referral source or a facility doctor. */
export function referralName(id, doctors) {
  if (isCustomReferral(id)) return String(id).slice(CUSTOM_PREFIX.length);
  return [...REFERRAL_SOURCES, ...doctors].find(
    (d) => String(d.doctor_id) === String(id),
  )?.name;
}

export const toCustomId = (name) => `${CUSTOM_PREFIX}${cleanName(name)}`;

/**
 * ordered_by_doctor_id sent to the backend: "SELF" / "EXTERNAL" for the two sources,
 * "CUSTOM:<name>" for a typed name, otherwise the doctor_id itself.
 */
export const toOrderedByPayload = (id) => {
  if (isReferralSource(id)) return id.replace(/_/g, "").toUpperCase();
  if (isCustomReferral(id)) return `CUSTOM:${referralName(id, [])}`;
  return id;
};

/** Prompt for a free-text name; `onSubmit` gets the cleaned name. */
export function CustomNameDialog({ open, title, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const close = () => {
    setName("");
    onClose();
  };
  const submit = () => {
    const cleaned = cleanName(name);
    if (!cleaned) return;
    onSubmit(cleaned);
    close();
  };
  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="xs">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          size="small"
          margin="dense"
          placeholder="Enter name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          inputProps={{ maxLength: 100 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!cleanName(name)}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const filterDoctors = createFilterOptions({ stringify: formatDoctorName });

/**
 * Referring doctors, chosen only from the facility doctor list. `value` is an array of
 * doctor_ids; `onChange` receives the new array of doctor_ids.
 */
export default function ReferredByDoctorField({
  value,
  onChange,
  doctors: doctorsProp,
  loading: loadingProp,
  locked = false,
}) {
  const own = useFacilityDoctors(doctorsProp === undefined);
  const doctors = doctorsProp !== undefined ? doctorsProp : own.doctors;
  const loading = doctorsProp !== undefined ? !!loadingProp : own.loading;

  const options = useMemo(
    () => [
      ...REFERRAL_SOURCES,
      CUSTOM_OPTION,
      ...doctors.filter((doctor) => doctor.doctor_id),
    ],
    [doctors],
  );
  const selected = useMemo(
    () =>
      (value || [])
        .map((id) =>
          isCustomReferral(id)
            ? { doctor_id: id, name: referralName(id, []) }
            : options.find((doctor) => String(doctor.doctor_id) === String(id)),
        )
        .filter(Boolean),
    [options, value],
  );

  const [customOpen, setCustomOpen] = useState(false);

  return (
    <>
      <Autocomplete
        id="referredBy"
        multiple
        filterSelectedOptions
        size="small"
        fullWidth
        options={options}
        loading={loading}
        value={selected}
        filterOptions={filterDoctors}
        getOptionDisabled={() => locked}
        getOptionLabel={formatDoctorName}
        isOptionEqualToValue={(option, optionValue) =>
          String(option.doctor_id) === String(optionValue.doctor_id)
        }
        noOptionsText={loading ? "Loading doctors..." : "No doctors found"}
        onChange={(_, doctors) => {
          const ids = doctors.map((d) => d.doctor_id);
          // "Custom name..." opens a prompt instead of becoming a chip itself.
          if (ids.includes(CUSTOM_OPTION.doctor_id)) {
            setCustomOpen(true);
            return;
          }
          onChange(ids);
        }}
        ListboxProps={{ style: { maxHeight: 280 } }}
        renderOption={(props, doctor) => (
          <li {...props} key={doctor.doctor_id}>
            <Typography variant="body2">{formatDoctorName(doctor)}</Typography>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              selected.length
                ? ""
                : loading
                  ? "Loading doctors..."
                  : "Select referring doctors"
            }
            inputProps={{
              ...params.inputProps,
              name: "referredBy",
              autoComplete: "off",
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                minHeight: "38px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#ced4da",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#125386",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#125386",
                  borderWidth: "1.5px",
                },
              },
            }}
          />
        )}
      />
      <CustomNameDialog
        open={customOpen}
        title="Custom referring name"
        onClose={() => setCustomOpen(false)}
        onSubmit={(name) => onChange([...(value || []), toCustomId(name)])}
      />
    </>
  );
}
